// login.tsx
import React, { useRef, useEffect } from 'react'
import {
  Grid,
  Button,
  Input,
  FormControl,
  InputLabel,
  Grow,
  Modal,
  Select,
  MenuItem,
  FormControlLabel,
  styled,
  Checkbox,
  Tooltip,
  InputAdornment,
  IconButton
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  CrearCuentaUsuarioExterno,
  UserLogin,
  saveUserData,
  saveUserIDs,
  validarEmail,
  validarTel
} from '../hooks/login'

import '../styles/login.css'
import { navigate } from 'gatsby'
import { AlertaStack, Spinner } from './components'

import RecoveryPasswordActivateAccount from './recoveryPassword'
import { useForm } from 'react-hook-form'

import ReCAPTCHA from 'react-google-recaptcha'
import CloseButton from './close-button'
import PropTypes from 'prop-types'
import { usePageContext } from '../context'
import SheetGrid from './sheet-grid/sheet-grid'
import { tooltipClasses, type TooltipProps } from '@mui/material/Tooltip'
import { Box, Container, TextField, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {
  createTheme,
  ThemeProvider,
  Theme,
  useTheme
} from '@mui/material/styles'
import OutlinedInput, { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { SmsConsent } from './sms-dialog'
import { useSingleType } from '../hooks/use-single-type'

type AlertVariant = 'default' | 'success' | 'error'

interface LoginProps {
  setSession: (value: boolean) => void
  setOpenLogin?: (value: boolean) => void
  view?: boolean
  onLogin?: (message: string, variant: AlertVariant) => void
  loginView?: boolean
  closeModal?: () => void
  canCloseModal?: boolean
}

type FormValues = {
  // Login
  email: string
  password: string
  // Registro
  nombre: string
  apellidoPaterno: string
  lada: string
  telefono: string
  correo: string
}

let test = process.env.TESTING === 'true'

const HtmlTooltip = styled(
  ({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    width: '95%',
    maxWidth: '550px',
    fontSize: '10px',
    border: '1px solid #dadde9'
  }
}))

const BpIcon = styled('span')(({ theme }: { theme: Theme }) => ({
  borderRadius: 3,
  width: 21,
  height: 21,
  margin: 4.5,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px var(--primary), inset 0 -1px 0 var(--primary)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 2
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'dark' ? '#30404d' : '#ebf1f5'
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(57,75,89,.5)'
        : 'rgba(206,217,224,.5)'
  }
}))

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  height: '95%',
  maxHeight: '840px',
  maxWidth: '490px',
  padding: '20px'
}

const customTheme = (outerTheme: Theme) =>
  createTheme({
    palette: {
      mode: outerTheme.palette.mode
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            background: '#FFFFFF',
            minHeight: '48px',
            marginBottom:'10px',
            '--TextField-brandBorderColor': '#aeaeaeff',
            '--TextField-brandBorderHoverColor': '#B2BAC2',
            '--TextField-brandBorderFocusedColor': 'var(--primary)',
            '& label.Mui-focused': {
              color: 'var(--TextField-brandBorderFocusedColor)'
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
            
          notchedOutline: {
            borderColor: 'var(--TextField-brandBorderColor)',
            top: '-10px'
          },
          root: {
             minHeight: '48px',
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'var(--TextField-brandBorderHoverColor)'
            },
            [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: 'var(--TextField-brandBorderFocusedColor)'
            },
            '--TextField-brandBorderColor': '#aeaeaeff',
            '--TextField-brandBorderHoverColor': '#B2BAC2',
            '--TextField-brandBorderFocusedColor': 'var(--primary)',
            '& label.Mui-focused': {
              color: 'var(--TextField-brandBorderFocusedColor)'
            }
          }
        }
      },
      // MuiFilledInput: {
      //   styleOverrides: {
      //     root: {
      //       '&::before, &::after': {
      //         borderBottom: '2px solid var(--TextField-brandBorderColor)'
      //       },
      //       '&:hover:not(.Mui-disabled, .Mui-error):before': {
      //         borderBottom: '2px solid var(--TextField-brandBorderHoverColor)'
      //       },
      //       '&.Mui-focused:after': {
      //         borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)'
      //       }
      //     }
      //   }
      // },
      MuiInput: {
        styleOverrides: {
          root: {
            '&::before': {
              borderBottom: '2px solid var(--TextField-brandBorderColor)',
              borderRadius: `24px`
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
              borderRadius: `24px`
            },
            '&.Mui-focused:after': {
              borderBottom:
                '2px solid var(--TextField-brandBorderFocusedColor)',
              borderRadius: `24px`
            }
          }
        }
      }
    }
  })

const Login: React.FC<LoginProps> = ({
  setSession,
  setOpenLogin,
  view,
  onLogin = () => {},
  loginView = false,
  closeModal = () => {},
  canCloseModal = true
}) => {
  const outerTheme = useTheme()
  const pageContext = usePageContext()
  const [loading, setLoading] = React.useState<boolean>(false)
  const [loginVisible, setLoginVisible] = React.useState<boolean>(false)
  const [registroVisible, setRegistroVisible] = React.useState<boolean>(false)
  const [eyePass, setEyePass] = React.useState<boolean>(false)
  const [message, setMessage] = React.useState<string>('')
  const [messageLogin, setMessageLogin] = React.useState<string>('')
  const [alertVariant, setAlertVariant] =
    React.useState<AlertVariant>('default')
  const [email, setEmail] = React.useState<string>('')
  const [cliente, setCliente] = React.useState<any>(null)
  const [newAlert, setNewAlert] = React.useState<number>(0)
  const [accion, setAccion] = React.useState<string>('')

  const { register, handleSubmit } = useForm<FormValues>()

  const [open, setOpen] = React.useState<boolean>(false)

  const [renderCaptcha, setRenderCaptcha] = React.useState<boolean>(false)
  const [accepted, setAccepted] = React.useState<boolean>(false)
  const [acceptedTyC, setAcceptedTyC] = React.useState<boolean>(false)

  const [openBooking, setOpenBooking] = React.useState(false)
  const handleOpenBooking = () => setOpenBooking(true)
  const handleCloseBooking = () => setOpenBooking(false)
  const [data, setData] = React.useState<any>({"background":{"Image":{"url":"/static-files/images/playa.jpg"},"Caption":"Las Puertas D' Mazatlán"}});

  let testUser: any

    useSingleType({
      name: "modal-login",
      population: ["background.Image"],
      onSuccess(result) {
        setData(result);
        // setBg(result?.background?.Image?.url);
        // console.log("Data Modal Login:", result);
      },
      onError(error) {
          console.log(error);            
      },
      onComplete() {
        // setLoaded(true);
      },
    });
  const changeToRegistro = () => {
    if (!view) {
      setLoginVisible(false)
      setRegistroVisible(true)
    } else {
      navigate('/registro')
    }
  }

  const changeToLogin = () => {
    if (!view) {
      setLoginVisible(true)
      setRegistroVisible(false)
    } else {
      navigate('/login')
    }
  }

  const mostrarPassword = () => setEyePass(!eyePass)

  const recaptcha = useRef<ReCAPTCHA | null>(null)

  const onSubmit = async (data: FormValues) => {
    if (process.env.NODE_ENV !== 'development' && !test) {
      const captchaValue = recaptcha.current?.getValue()
      if (!captchaValue) {
        alert('Favor de verificar reCAPTCHA')
        return
      }
    }
    registro(data)
  }

  const submitLogin = (data: FormValues) => {
    login(data)
  }

  function handleFetchError (error: Error) {
    setMessage(error.message)
    setNewAlert(value => (value ? value + 1 : 1))
    setLoading(false)
    console.log(error)
  }

  useEffect(() => {
    if (loginView) {
      setLoginVisible(true)
      setRegistroVisible(false)
    } else {
      setLoginVisible(false)
      setRegistroVisible(true)

      setTimeout(() => {
        setRenderCaptcha(true)
      }, 800)
    }

    if (cliente !== undefined && cliente !== null && cliente.cliente !== 0) {
      window.sessionStorage.setItem('cliente', cliente.cliente)
      window.sessionStorage.setItem('email', email)
      setSession(true)
    } else if (cliente != null) {
      // no-op
    }
  }, []) // ojo: si quieres que reaccione a "cliente", deberías añadirlo al array de deps

  const login = async (data: FormValues) => {
    if (!test) {
      setLoading(true)
      setMessageLogin('Iniciando sesión')
      setEmail(data.email)
      LoginBD(data.email, data.password)
    } else {
      testEnvLogin().finally(() => {
        onLogin('Ha iniciado sesión correctamente', 'success')
        if (setOpenLogin) {
          setOpenLogin(false)
        }
        setSession(true)
        disableLoginTimeOut()
      })
    }
  }

  const LoginBD = async (email: string, pass: string) => {
    await UserLogin(email, pass).then(
      (resp: any) => {
        if (resp.cliente > 0) {
          onLogin('Sesion iniciada', 'success')
          setCliente(resp.cliente)
          setMessage('Sesion iniciada')
          disableLoginTimeOut()

          window.sessionStorage.setItem('cliente', resp.cliente)
          if (setOpenLogin) {
            setOpenLogin(false)
          }
          setSession(true)
          setLoading(false)
        } else {
          setMessage('Datos incorrectos')
          setAlertVariant('error')
          setNewAlert(value => value + 1)
          setLoading(false)
        }
      },
      (reason: unknown) => {
        console.log(reason)
      }
    )
  }

  const registro = async (data: FormValues) => {
    setLoading(true)
    setMessageLogin('Iniciando registro')

    if (!test) {
      // Validando Teléfono
      try {
        const resp: any = await validarTel(data.lada + data.telefono)
        if (resp.Codigo !== 0 && resp.Codigo < 500) {
          throw new Error(resp.Mensaje)
        }
      } catch (error: any) {
        handleFetchError(error)
        return
      }

      // Proceso de Registro
      setMessage('Validando Correo Electrónico')

      await validarEmail(data.correo)
        .then(async (resp: any) => {
          if (resp.Codigo === 0) {
            setMessage('Validando Correo Electrónico')

            await CrearCuentaUsuarioExterno(data)
              .then((resp: any) => {
                if (resp.Code === 200) {
                  sessionStorage.setItem('nombre', data.nombre)
                  sessionStorage.setItem('RegistroAlert', 'true')
                  onLogin(
                    'Registro exitoso, se enviaron sus credenciales a su correo electrónico.',
                    'success'
                  )

                  if (setOpenLogin) {
                    setOpenLogin(false)
                  }
                  setSession(true)
                  disableLoginTimeOut()
                } else if (resp.Code === 400) {
                  throw new Error(
                    '¡Ya estás registrado! Por favor, active su cuenta para iniciar sesion'
                  )
                } else if (resp.Code === 401) {
                  throw new Error(
                    'La cuenta que esta intentando registrar ya existe, si aún no recibe sus credenciales en su correo electrónico comuníquese con Atención a Clientes.'
                  )
                } else {
                  throw new Error(
                    'Ocurrio un error con el registro, contacte a Servicio al Cliente.'
                  )
                }
              })
              .catch((reason: any) => {
                handleFetchError(reason)
              })
          } else {
            throw new Error(resp.Mensaje)
          }
        })
        .catch((error: any) => {
          handleFetchError(error)
        })
    } else {
      testEnvLogin().finally(() => {
        onLogin(
          'Registro exitoso, se enviaron sus credenciales a su correo electrónico.',
          'success'
        )
        if (setOpenLogin) {
          setOpenLogin(false)
        }
        setSession(true)
        disableLoginTimeOut()
      })
    }
  }

  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick') {
      setOpen(false)
    }
  }

  const MobileActivateAccount: React.FC = () => (
    <Grid
      id='mobileActivateAccount'
      className={'divRegistroLabels'}
      style={{ background: '#e6ebf3', padding: 15, borderRadius: 5 }}
    >
      <Button
        id='iniciarSesionButtonLoginForm'
        variant='text'
        onClick={() => {
          setOpen(true)
          setAccion('activate')
        }}
        type='button'
        style={{ padding: 0 }}
      >
        <p className={'color-primary'} style={{ margin: 0 }}>
          <span style={{ color: '#4e4e4e' }}>¿Ya eres cliente?</span> Activa tu
          cuenta
        </p>
      </Button>
    </Grid>
  )

  const ViewLogin: React.FC = () => (
    <form className='loginForm' onSubmit={handleSubmit(submitLogin)}>
      <Grid>
        <p className={'titleLogin'}> Iniciar sesión</p>
      </Grid>
      <div>
        <Grid>
          <ThemeProvider theme={customTheme(outerTheme)}>
            <label htmlFor='email'>Usuario<span style={{ color: 'red' }}>*</span></label>
            <TextField
              id='email'
              label={''}
              variant={'outlined'}
              size={'small'}
              color='primary'
              fullWidth
              {...register('email')}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='email'
              spellCheck={false}
              type={'email'}
              required={!test}
              style={{ marginTop: '4px' }}                           
              inputProps={{
                defaultValue: '',
                maxLength: 45,
                minLength: 10,
                pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$',
                inputMode: 'text',
                title: 'Por favor ingrese su correo electrónico.'
              }}
            />
          </ThemeProvider>
        </Grid>
        <Grid>
          <ThemeProvider theme={customTheme(outerTheme)} >
            <FormControl sx={{ width: '100%' }} variant="outlined" color='primary' >
              <label htmlFor='password'>Contraseña<span style={{ color: 'red' }}>*</span></label>
              <OutlinedInput
                id='password'
                label={''}
                color="primary"
                // variant={'outlined'}
                size={'small'}
                fullWidth
                {...register('password')}
                inputProps={{
                  defaultValue: '',
                  maxLength: 50,
                  minLength: 1,
                  inputMode: 'text',
                  title: 'Por favor ingrese su contraseña.'
                }}
                required={!test}
                name='password'
                placeholder=''
                spellCheck={false}
                type={eyePass ? 'text' : 'password'}
                style={{ marginTop: '4px', background: '#FFFFFF' }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={mostrarPassword}
                      edge="end"
                    >
                      {eyePass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </ThemeProvider>
          <Grid style={{ textAlign: 'right' }}>
            <button
              type='button'
              style={{
                fontFamily: 'Lato',
                background: 'none',
              }}
              className='recoverySpan'
              onClick={() => {
                setOpen(true)
                setAccion('password')
              }}
            >
              Recuperar Contraseña
            </button>
          </Grid>
        </Grid>
      </div>  
      <Grid className={'divBtn'} sx={{ flexDirection: 'column' }}>
          <Button
            className='btnLogin bounceButton'
            id='button-login'
            type='submit'
            sx={{ fontSize: 16, flexDirection: 'column' }}
          >
            Acceder al sitio
          </Button>
        </Grid>
      <div>
        <Grid className={'divRegistroLabels'}>
          <Button onClick={changeToRegistro} style={{ padding: 0 }}>
            <p className={'color-primary label-registrado'} >
              <span >¿No estás registrado?</span>{' '}
              Regístrate Aquí!
            </p>
          </Button>
          <br />
          <Button
            id='iniciarSesionButtonLoginForm'
            variant='text'
            onClick={() => {
              setOpen(true)
              setAccion('activate')
            }}
            type='button'
            style={{ padding: 0 }}>
            <p className={'color-primary label-registrado'}>
              <span>¿Ya eres cliente?</span> Activa tu
              cuenta
            </p>
          </Button>
        </Grid>
      </div>
    </form>
  )

  const ViewRegistro: React.FC = () => (
    <form id='registroForm' onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <p className={'titleLogin'}> Regístrate</p>
        <p className={'descripcion'}>
          Para ver valores, disponibilidad en tiempo real, ofertas de hasta un
          50% de descuento en el lote de terreno con sus términos y condiciones.
        </p>
        <p>
          Tu registro es necesario para garantizar seguridad y confidencialidad.
        </p>
      </Grid>

      <Grid container className='' spacing={0}>
        <Grid item xs={12} md={12}>
          {/* <FormControl variant='standard' style={{ width: '100%' }}>
            <InputLabel
              style={{
                marginTop: '10px',
                fontFamily: 'Lato, sans-serif',
                color: 'var(--color-primary)'
              }}
            >
              Nombre<span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <Input
              {...register('nombre', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='nombre'
              spellCheck={false}
              type={'text'}
              required={!test}
              inputProps={{
                maxLength: 45,
                minLength: 2,
                inputMode: 'text',
                pattern: '[a-zA-ZáéíóúÁÉÍÓÚñÑ\\s]+',
                title:
                  'Por favor ingrese su nombre. No ingrese números ni caracteres especiales.'
              }}
            />
          </FormControl> */}
          <ThemeProvider theme={customTheme(outerTheme)}>
            <label htmlFor='Nombre'>Nombre</label>
            <TextField
            id='nombreInput'
              label={''}
              variant={'outlined'}
              size={'small'}
              {...register('nombre', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='nombre'
              spellCheck={false}
              type={'text'}
              style={{ marginTop: '4px' }}
              fullWidth
              required={!test}
              inputProps={{
                maxLength: 45,
                minLength: 2,
                inputMode: 'text',
                pattern: '[a-zA-ZáéíóúÁÉÍÓÚñÑ\\s]+',
                title:
                  'Por favor ingrese su nombre. No ingrese números ni caracteres especiales.'
              }}
            />
          </ThemeProvider>
        </Grid>
        <Grid item xs={12} md={12}>
          {/* <FormControl variant='standard' style={{ width: '100%' }}>
            <InputLabel
              style={{
                marginTop: '10px',
                fontFamily: 'Lato, sans-serif',
                color: 'var(--color-primary)'
              }}
            >
              Apellido Paterno<span style={{ color: 'red' }}>*</span>
            </InputLabel>
            <Input
              {...register('apellidoPaterno', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='apellidoPaterno'
              spellCheck={false}
              type={'text'}
              required={!test}
              inputProps={{
                maxLength: 45,
                minLength: 3,
                inputMode: 'text',
                pattern: '[a-zA-ZáéíóúÁÉÍÓÚñÑ\\s]+',
                title:
                  'Por favor ingrese su apellido paterno. No ingrese números ni caracteres especiales.'
              }}
            />
          </FormControl> */}
          <ThemeProvider theme={customTheme(outerTheme)}>
            <label htmlFor='apellidoPaterno'>
              {' '}
              Apellido Paterno<span style={{ color: 'red' }}>*</span>
            </label>
            <TextField
              label={''}
              variant={'outlined'}
              size={'small'}
              fullWidth
              {...register('apellidoPaterno', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='apellidoPaterno'
              spellCheck={false}
              type={'text'}
              required={!test}
              inputProps={{
                maxLength: 45,
                minLength: 3,
                inputMode: 'text',
                pattern: '[a-zA-ZáéíóúÁÉÍÓÚñÑ\\s]+',
                title:
                  'Por favor ingrese su apellido paterno. No ingrese números ni caracteres especiales.'
              }}
            />
          </ThemeProvider>
        </Grid>
      </Grid>

      <Grid className='' display={'flex'}>
        {/* <FormControl
          variant='standard'
          style={{ width: '80px', height: '56px' }}
        >
          <InputLabel
            style={{
              marginTop: '10px',
              fontFamily: 'Lato, sans-serif',
              color: 'var(--color-primary)'
            }}
          >
            Lada<span style={{ color: 'red' }}>*</span>
          </InputLabel>

          <Select
            variant='standard'
            style={{ height: '100%' }}
            {...register('lada', { required: !test })}
            autoCapitalize='off'
            autoComplete='off'
            autoCorrect='off'
            margin='none'
            name='lada'
            spellCheck={false}
            required={!test}
            inputProps={{ defaultValue: 52 }}
            renderValue={(value: number) => {
              return (
                <Grid
                  item
                  style={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between'
                  }}
                >
                  {value === 52 && (
                    <img
                      style={{
                        margin: 'auto',
                        maxHeight: '15px',
                        marginLeft: '0px'
                      }}
                      src='/static-files/flag-mexico.png'
                      alt='Mexico Flag'
                    />
                  )}
                  {value === 1 && (
                    <img
                      style={{
                        margin: 'auto',
                        maxHeight: '15px',
                        marginLeft: '0px'
                      }}
                      src='/static-files/flag-eua.png'
                      alt='USA Flag'
                    />
                  )}
                  + {value}
                </Grid>
              )
            }}
          >
            <MenuItem
              value={52}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              +52
            </MenuItem>
            <MenuItem value={1}>+1</MenuItem>
          </Select>
        </FormControl>
        <FormControl style={{ width: 'calc(100% - 80px)' }}>
          <InputLabel
            style={{
              marginTop: '10px',
              fontFamily: 'Lato, sans-serif',
              color: 'var(--color-primary)'
            }}
          >
            Teléfono<span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Input
            {...register('telefono', { required: !test })}
            autoCapitalize='off'
            autoComplete='off'
            autoCorrect='off'
            margin='none'
            name='telefono'
            spellCheck={false}
            type={'tel'}
            required={!test}
            inputProps={{
              maxLength: 10,
              minLength: 10,
              pattern: '^[0-9]{10}',
              title:
                'El número de teléfono debe incluir únicamente números y debe tener 10 dígitos',
              inputMode: 'tel'
            }}
          />
        </FormControl> */}

        <ThemeProvider theme={customTheme(outerTheme)}>
          <div style={{ width: '100px', height: '56px' }}>
            <label htmlFor='lada'>
              Lada<span style={{ color: 'red' }}>*</span>
            </label>

            <TextField
              id='lada'
              select
              label='' // usamos el <label> de arriba
              variant='outlined'
              size='small'
              fullWidth
              defaultValue={52}
              {...register('lada', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='lada'
              spellCheck={false}
              required={!test}
              SelectProps={{
                renderValue: value => {
                  const v = value as number

                  return (
                    <Grid
                      item
                      style={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'space-between'
                      }}
                    >
                      {v === 52 && (
                        <img
                          style={{
                            margin: 'auto',
                            maxHeight: '15px',
                            marginLeft: '0px'
                          }}
                          src='/static-files/flag-mexico.png'
                          alt='Mexico Flag'
                        />
                      )}
                      {v === 1 && (
                        <img
                          style={{
                            margin: 'auto',
                            maxHeight: '15px',
                            marginLeft: '0px'
                          }}
                          src='/static-files/flag-eua.png'
                          alt='USA Flag'
                        />
                      )}
                      +{v}
                    </Grid>
                  )
                }
              }}
            >
              <MenuItem
                value={52}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                +52
              </MenuItem>
              <MenuItem value={1}>+1</MenuItem>
            </TextField>
          </div>
        </ThemeProvider>
        <ThemeProvider theme={customTheme(outerTheme)}>
          <div style={{ width: 'calc(100% - 80px)' }}>
            <label htmlFor='telefono'>
              Teléfono<span style={{ color: 'red' }}>*</span>
            </label>

            <TextField
              id='telefono'
              label='' // usamos el <label> de arriba
              variant='outlined'
              size='small'
              fullWidth
              type='tel'
              {...register('telefono', { required: !test })}
              autoCapitalize='off'
              autoComplete='off'
              autoCorrect='off'
              margin='none'
              name='telefono'
              spellCheck={false}
              required={!test}
              inputProps={{
                maxLength: 10,
                minLength: 10,
                pattern: '^[0-9]{10}',
                title:
                  'El número de teléfono debe incluir únicamente números y debe tener 10 dígitos',
                inputMode: 'tel'
              }}
            />
          </div>
        </ThemeProvider>
      </Grid>

      <Grid className=''>
        {/* <FormControl variant='standard' style={{ width: '100%' }}>
          <InputLabel
            style={{
              marginTop: '10px',
              fontFamily: 'Lato, sans-serif',
              color: 'var(--color-primary)'
            }}
          >
            Correo electrónico<span style={{ color: 'red' }}>*</span>
          </InputLabel>
          <Input
            {...register('correo', { required: !test })}
            autoCapitalize='off'
            autoComplete='off'
            autoCorrect='off'
            margin='none'
            name='correo'
            spellCheck={false}
            type={'email'}
            required={!test}
            inputProps={{
              maxLength: 75,
              minLength: 10,
              pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$',
              inputMode: 'email',
              title: 'Por favor ingrese su correo electrónico.'
            }}
          />
        </FormControl> */}

        <ThemeProvider theme={customTheme(outerTheme)}>
          <label htmlFor='Correo electrónico'>Correo electrónico<span style={{ color: 'red' }}>*</span></label>

          <TextField
            label={''}
            variant={'outlined'}
            size={'small'}
            style={{ marginTop: '4px' }}
            fullWidth
            {...register('correo', { required: !test })}
            autoCapitalize='off'
            autoComplete='off'
            autoCorrect='off'
            margin='none'
            name='correo'
            spellCheck={false}
            type={'email'}
            required={!test}
            inputProps={{
              maxLength: 75,
              minLength: 10,
              pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$',
              inputMode: 'email',
              title: 'Por favor ingrese su correo electrónico.'
            }}
          />
        </ThemeProvider>
      </Grid>

      <SmsConsent accepted={accepted} setAccepted={setAccepted} ></SmsConsent>


      <FormControlLabel
        className='avisoMensajeriaCheck'
        sx={{
          '& .MuiSvgIcon-root': { fontSize: '30px !important' }
        }}
        control={
          <Checkbox
            checked={acceptedTyC}
            icon={<BpIcon />}
            onChange={() => {
              setAcceptedTyC(!acceptedTyC)
            }}
          />
        }
        label='Al registrarte aceptas nuestros Términos y Condiciones y Aviso de Privacidad.'
      />

      <div>
        {process.env.NODE_ENV !== 'development' && !test && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 8
            }}
          >
            <ReCAPTCHA
              ref={recaptcha}
              sitekey={'6LcKnFopAAAAAFmbtebFHc86PsyDocCSXp07o8oL'}
            />
          </div>
        )}
        <Grid className={'divBtn'}>
          <Button
            className='btnLogin bounceButton'
            type='submit'
            value={'Registro'}
            id='button-login'
            sx={{ fontSize: 16 }}
          >
            ACCEDER AL SITIO
          </Button>
          <Button
            className='btnLogin btnAgente'
            type='button'
            value={'Registro'}
            id='button-agente'
            sx={{ fontSize: 16 }}
            onClick={handleOpenBooking}
          >
            Hablar con un agente
          </Button>
        </Grid>
        <Grid className={'divRegistroLabels'}>
          <Button
            id='iniciarSesionButtonLoginForm'
            variant='text'
            onClick={changeToLogin}
            type='button'
            className='btn_registrado'
          >
            <p className={'color-primary label-registrado'}>
              <span>¿Ya estás registrado?</span>{' '}
              Iniciar sesión
            </p>
          </Button>
          <p className='certificacionLabel'>
            <span>*</span> Certificado TLS/SSL Secure Site EV de DigiCert, con
            validación extendida y garantía corporativa.
          </p>
        </Grid>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'nowrap', // keep a single horizontal row
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 25,
              width: '100%'
            }}
          >
            <img src="/static-files/images/logos_maverick/certificaciones_maverick.png" alt="Certificaciones Maverick" style={{maxWidth: "80%"}} />
          </div>
      </div>
    </form>
  )

  function disableLoginTimeOut () {
    pageContext?.setCanCloseModal(true)
    localStorage.removeItem('notLoggedTimeOut')
  }

  async function testEnvLogin () {
    const date = new Date()
    const dateString =
      date.getDay().toString() +
      date.getHours().toString() +
      date.getMinutes().toString() +
      date.getSeconds().toString()

    return fetch(`${process.env.DATA_API_URL}/test-user.json?v=${dateString}`, {
      method: 'GET'
    })
      .then(response => response.json())
      .then(response => {
        testUser = response
        saveUserData(testUser)
        saveUserIDs(
          testUser.DatosCliente.IdUsuario,
          testUser.DatosCliente.IdCte
        )
      })
  }

  return (
    <React.Fragment>
      <Modal
        className={'modal'}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <RecoveryPasswordActivateAccount
          // tabIndex={-1}
          open={open}
          setOpen={setOpen}
          accion={accion}
        />
      </Modal>

      {newAlert > 0 && (
        <AlertaStack
          message={message}
          newAlert={newAlert}
          variant={alertVariant}
        />
      )}

      {loading && !setOpenLogin && (
        <Spinner
          mensaje={messageLogin}
          spinnerType={'loginSpinner'}
          sizePage={true}
          classSizePage={'loginPageContainer'}
          backgroundColor={'#0000001a'}
          widthPage={undefined}
          borderRaius={undefined}
        />
      )}

      <Grow in={true} timeout={{ enter: 500 }}>
        <Grid container className={'loginContainer '} style={{backgroundImage: `url(${data?.background?.Image?.url})`}}>
          {loading && setOpenLogin && (
            <Spinner
              mensaje={messageLogin}
              spinnerType={'loginSpinner'}
              borderRaius={'15px'}
              widthPage={true}
              sizePage={true}
              classSizePage={'loginContainer'}
              backgroundColor={'#0000001a'}
            />
          )}

          {canCloseModal && <CloseButton onClick={closeModal} />}

          {/* <Grid
            item
            lg={6}
            md={6}
            style={{ display: 'none' }}
            className={'divLogoContainer'}
            /> */}

            <Grid item sx={{ display: { xs: 'none', md: 'flex' }}} className='divPlecaLogin'><p className='plecaDualBlock plecaLogin'>{data?.background?.Caption}</p></Grid>
          {/* <SheetGrid
            className='divLogin'
            style={{ minHeight: '648px', width: '100%', maxWidth: '500px' }}
          > */}
          <div className='divLogin'>
            {/* {canCloseModal && <CloseButton onClick={closeModal} />} */}
            <Grid
              className={'w-100 DesignLogin'}
              style={{ maxWidth: '550px', height: '100%' }}>
              {loginVisible && <ViewLogin />}
              {registroVisible && <ViewRegistro />}
              {/* <MobileActivateAccount /> */}
            </Grid>
          </div>
          {/* </SheetGrid> */}
        </Grid>
      </Grow>
      <Modal className={'modal'} keepMounted open={openBooking} onClose={handleCloseBooking}>
        <Grid sx={{ ...ModalStyle }}>
          <div>
            <div className={'divButton'}>
              <Button onClick={handleCloseBooking}>
                <CloseIcon></CloseIcon>
              </Button>
            </div>
            <iframe
              title={'Agenda tu cita'}
              className="iframeBooking"
              src={`https://outlook.office.com/book/LasPuertasdeMazatlan@NETORGFT1759235.onmicrosoft.com/?ismsaljsauthenabled`}
            ></iframe>
          </div>
        </Grid>
      </Modal>
    </React.Fragment>
  )
}

export default Login
;(Login as any).propTypes = {
  setSession: PropTypes.func,
  setOpenLogin: PropTypes.func,
  view: PropTypes.any,
  onLogin: PropTypes.func,
  loginView: PropTypes.bool,
  closeModal: PropTypes.func,
  canCloseModal: PropTypes.bool
}
